import { useMutation } from "@apollo/client";
import Add from "@mui/icons-material/Add";
import Check from "@mui/icons-material/Check";
import Edit from "@mui/icons-material/Edit";
import { LoadingButton } from "@mui/lab";
import { Alert, Button, Paper, Stack, TextField, Typography, useTheme } from "@mui/material";
import { gql } from "generated-graphql";
import { useState } from "react";

import { useFulfillmentDetails } from "./useFulfillmentDetails";

const UPDATE_FULFILLMENT_NOTE_MUTATION = gql(`
  mutation UpdateTierIIReportFulfillmentNote($id: ID!, $note: String) {
    updateTierIIReportFulfillmentNote(id: $id, note: $note) {
      id
      fulfillmentNote
    }
  }
`);

export function FulfillmentNote() {
  const theme = useTheme();
  const { data } = useFulfillmentDetails();
  const [isEditing, setIsEditing] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [error, setError] = useState<string | null>(null);

  const reportId = data?.tierIIReport?.id;
  const currentNote = (data?.tierIIReport as any)?.fulfillmentNote;

  const [updateNote, { loading: updating }] = useMutation(UPDATE_FULFILLMENT_NOTE_MUTATION, {
    onCompleted: () => {
      setIsEditing(false);
      setError(null);
    },
    onError: (error) => {
      setError(error.message);
    },
  });

  const handleEditClick = () => {
    setNoteText(currentNote || "");
    setIsEditing(true);
  };

  const handleCancelClick = () => {
    setNoteText("");
    setIsEditing(false);
  };

  const handleSaveClick = async () => {
    if (!reportId) return;

    await updateNote({
      variables: {
        id: reportId,
        note: noteText || null, // Send null to clear the note
      },
    });
  };

  if (!reportId) return null;

  const hasNote = Boolean(currentNote);

  const noteContent = (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" spacing={1} alignItems="center">
          <span role="img" aria-label="pin">
            📌
          </span>
          <Typography variant="h6">Fulfillment Note</Typography>
        </Stack>
        {isEditing ? (
          <Stack direction="row" spacing={1}>
            <Button color="error" onClick={handleCancelClick} disabled={updating}>
              CANCEL
            </Button>
            <LoadingButton
              color="success"
              startIcon={<Check />}
              onClick={handleSaveClick}
              loading={updating}
            >
              SAVE
            </LoadingButton>
          </Stack>
        ) : (
          <Button
            startIcon={<Edit />}
            onClick={handleEditClick}
            sx={{ color: theme.palette.secondary.main }}
          >
            EDIT NOTE
          </Button>
        )}
      </Stack>

      {isEditing ? (
        <TextField
          label="Note"
          multiline
          fullWidth
          minRows={2}
          maxRows={10}
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          placeholder="Add note"
        />
      ) : (
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
          {currentNote}
        </Typography>
      )}
    </Stack>
  );

  return (
    <Stack spacing={2}>
      {error && (
        <Alert severity="error">
          <Typography variant="body2">Error: {error}</Typography>
        </Alert>
      )}
      {hasNote || isEditing ? (
        <Alert
          severity="warning"
          icon={false}
          sx={{
            p: 2,
            width: "100%",
            "& .MuiAlert-message": {
              width: "100%",
              padding: 0,
            },
          }}
        >
          {noteContent}
        </Alert>
      ) : (
        <Paper
          elevation={1}
          sx={{
            p: 2,
            backgroundColor: theme.palette.background.paper,
            width: "100%",
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              Record any bespoke reporting needs here.
            </Typography>
            <Button
              variant="text"
              startIcon={<Add />}
              onClick={handleEditClick}
              sx={{ textTransform: "none" }}
            >
              ADD NOTE
            </Button>
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}
